import { addMonths, format, isAfter } from "date-fns";
import filterFalsyParameters from "../../../common/helpers/filterFalsyParameters";
import filterFalsySalaryParameters from "../../../common/helpers/filterFalsySalaryParameters";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import isEmpty from "../../../common/helpers/isEmpty";
import { NameIdOption } from "../../../common/types/commonTypes";
import {
  AccumDateData, AccumDateRow,
  AddAccumDelta,
  EditEmpSalary,
  KtaAvg, SalaryValues,
  SetEmpAttrSalaryParams,
} from "../../employment/employmentApi";
import { ApplyTesSalaryTableCellParams } from "../../tesSalaryTables/tesSalaryTablesApi";
import {
  EmploymentSalaryRow,
  GroupedEmploymentSalaryRow,
  KtaRow,
  SalaryIncreaseStatus,
  SalaryTableRow,
  ShiftSalaryRow,
} from "../types/common";
import {
  BulkSalaryIncreaseQuery,
  EmploymentSalary,
  Kta,
  MassUpdateStatusRowId,
  ShiftSalary,
} from "../types/queries";
import { SalaryPerDate } from "../../employment/employmentApi";
import {
  BULK_SALARY_INCREASE_TARGETS,
  OPTION_DONT_CARE,
  SHIFT_SALARY_PAGE_SIZE,
} from "./constants";
import { BulkSalaryIncreaseFilterValues } from "./types";

export function isShiftSalary(salary: any): salary is ShiftSalary {
  return (
    'employmentId' !in salary
  );
}

export function isSalary(salary: any): salary is EmploymentSalary | ShiftSalary {
  return isEmploymentSalary(salary) || isShiftSalary(salary);
}

export function isEmploymentSalary(salary: any): salary is EmploymentSalary {
  return (
    typeof salary.employmentId === "string" &&
    typeof salary.employerName === "string"
  );
}

// TODO: is these needed?
export function isShiftSalaryTableRow(salary: any): salary is ShiftSalaryRow {
  return (
    typeof salary.talentId === "string" &&
    typeof salary.workTitle?.id === "string"
  );
}

export function isKtaRow(salary: any): salary is KtaRow {
  return (
      typeof salary === "object" &&
      salary !== null &&
      typeof salary.version === "undefined" // !("version" in salary)
  );
}

export function isEmploymentSalaryTableRow(
  salary: any,
): salary is EmploymentSalaryRow {
  return (
    typeof salary.employmentId === "string" &&
    typeof salary.employerName === "string" &&
    typeof salary.raiseDateKta !== "object" 
  );
}

export function isGroupedEmploymentSalaryRow(
  salary: any,
): salary is GroupedEmploymentSalaryRow { //{ employmentRows: EmploymentSalaryRow[] } {
  return (
    typeof salary === "object" &&
    salary !== null &&
    Array.isArray(salary.groupedEmploymentRows)
  );
}

// TODO: this might not be needed if chip filters are not used
export const formatFilterDefaultValues = (): BulkSalaryIncreaseFilterValues => {
  return {
    target: BULK_SALARY_INCREASE_TARGETS.SHIFT_SALARIES, //  EMPLOYMENT_SHIFTS
    isIncreaseApplied: "false",
    hasContEmp: OPTION_DONT_CARE,
    baseEqualsGroup: OPTION_DONT_CARE,
    salaryType: "HOURLY",
    workTimeType: [],
    // tes: {id: "tes_id_kunta_ala_ttes", name: "Kunta-alan tuntipalkkalaiset (TTES)"},
    tes: {id: "tes_id_marava", name: "Matkailu- ravintola- ja vapaa-ajan palvelut (työntekijät)"},
    salaryDateRange: [null, null],
    salaryIncreaseStartDate: new Date("2026-06-01"),
    workTitles: [],
    salaryComponents: [],
    ktaComponents: [],
    ktaKeskiansioComponents: [],
    isUnclear: OPTION_DONT_CARE,
    tesSalaryTableSourceId: "",
    tesSalaryTableTargetId: "",
    tesSalaryTableId: "",
    tesSelectionAtOrAfterRaise: OPTION_DONT_CARE,
    tesSelectionModes: [],
  };
};

export const formatSalaryIncreaseValues = (
  salaryIncrease: Record<string, string>,
): Record<string, string> => {
  return Object.entries(salaryIncrease).reduce(
    (acc, [key, value]) => {
      return {
        ...acc,
        ...(value ? {[key]: value.replace(/,/g, ".")} : {}),
      };
    },
    {} as Record<string, string>,
  );
};

export const formatToQuery = (
  filters: BulkSalaryIncreaseFilterValues, cursor: string | null = null
): BulkSalaryIncreaseQuery => {
  // backend accepts any salary range fields with salaryFrom or salaryTo prefixes
  const pickedSalaryRanges: Record<string, string | number> = {};
  Object.entries(filters).filter(([key, value]) => key.startsWith("_") && value).forEach(([key,value]) => {
    if (!!value.from) {
      pickedSalaryRanges[`salaryFrom${key}`] = value.from;
    }
    if (!!value.to) {
      pickedSalaryRanges[`salaryTo${key}`] = value.to;
    }
  });
  return {
    isIncreaseApplied: filters.isIncreaseApplied,
    target: filters.target,
    ...(filters.hasContEmp && filters.hasContEmp !== OPTION_DONT_CARE ? {hasContEmp: filters.hasContEmp} : {}),
    ...(filters.isUnclear && filters.isUnclear !== OPTION_DONT_CARE ? {isUnclear: filters.isUnclear} : {}),
    ...(filters.baseEqualsGroup && filters.baseEqualsGroup !== OPTION_DONT_CARE ? {baseEqualsGroup: filters.baseEqualsGroup} : {}),
    tesId: filters?.tes?.id as string,
    ...(filters.salaryDateRange?.[0] ? { salaryStartDate: format(filters.salaryDateRange[0] as Date, isoFormat)} : {}),
    ...(filters.salaryDateRange?.[1] ? {salaryEndDate: format(filters.salaryDateRange[1] as Date, isoFormat)} : {}),
    raiseDate: format(filters.salaryIncreaseStartDate as Date, isoFormat),
    ...(filters.salaryType ? {salaryType: filters.salaryType as string} : {}), // SalaryType enum
    ...(filters.salaryType === "MONTHLY"
      ? { workTimeType: filters.workTimeType.join(",") }
      : {}),
    ...(!isEmpty(filters.workTitles) && {
      workTitleIds: filters.workTitles
        .map((workTitle) => workTitle.id)
        .join(","),
    }),
    ...(filters.target === BULK_SALARY_INCREASE_TARGETS.SHIFT_SALARIES && { pageSize: `${SHIFT_SALARY_PAGE_SIZE}` }),
    ...(cursor && { cursor }),
    ...pickedSalaryRanges,
    ...(filters.target === BULK_SALARY_INCREASE_TARGETS.TES_TABLES &&
    filters.tesSalaryTableSourceId &&
    filters.tesSalaryTableTargetId
      ? {
          tesSalaryTableSourceId: filters.tesSalaryTableSourceId,
          tesSalaryTableTargetId: filters.tesSalaryTableTargetId,
        }
      : {}),
    ...(filters.target !== BULK_SALARY_INCREASE_TARGETS.TES_TABLES &&
    filters.tesSalaryTableId
      ? { tesSalaryTableId: filters.tesSalaryTableId }
      : {}),
    ...(filters.target !== BULK_SALARY_INCREASE_TARGETS.TES_TABLES &&
    filters.tesSelectionAtOrAfterRaise &&
    filters.tesSelectionAtOrAfterRaise !== OPTION_DONT_CARE
      ? { tesSelectionAtOrAfterRaise: filters.tesSelectionAtOrAfterRaise }
      : {}),
    ...(filters.target !== BULK_SALARY_INCREASE_TARGETS.TES_TABLES &&
    filters.tesSelectionModes &&
    filters.tesSelectionModes.length > 0
      ? { tesSelectionModes: filters.tesSelectionModes.join(",") }
      : {}),
    // mock: "true",
  };
};

export const latestAccumRow = (accumData: AccumDateData): AccumDateRow => {
  return accumData.rows[accumData.rows.length - 1];
}

export const formatShiftSalaryToCommand = (
  row: ShiftSalaryRow,
  filterValues: BulkSalaryIncreaseFilterValues,
  bulkUpdateId: string,
): SetEmpAttrSalaryParams => {
  const tesId = (filterValues.tes as NameIdOption).id;
  const from = format(
    new Date(filterValues.salaryIncreaseStartDate as Date),
    isoFormat,
  );

  const filteredSalaryIncrease = filterFalsySalaryParameters(
    formatSalaryIncreaseValues(row.salaryIncrease),
  );

  const zoned = filteredSalaryIncrease['zonedSalary'] as Record<string, SalaryValues>
  const zonedSalary = Object.keys(zoned).length === 0 ? undefined : zoned;

  return {
    talentId: row.talentId,
    version: row.version,
    workTitleId: row.workTitle.id,
    tesId,
    from,
    massUpdateId: bulkUpdateId,
    salary: filteredSalaryIncrease['salary'] as SalaryValues,
    ...(zonedSalary && {zonedSalary}),
    // zonedSalary: filteredSalaryIncrease['zonedSalary'] as Record<string, SalaryValues>
  };
};

export const formatEmploymentSalaryToCommand = (
  row: EmploymentSalaryRow,
  filterValues: BulkSalaryIncreaseFilterValues,
  bulkUpdateId: string,
  newSalaryComponents?: Record<string, string>,
): EditEmpSalary => {
  // Salary date can be earliest the salary date to not make
  // too early salary rows for long employments
  const raiseDate = filterValues.salaryIncreaseStartDate as Date;
  const salaryDate = row.raiseDateSalary.date;
  const isAfterRaiseDate = isAfter(salaryDate, raiseDate);
  const newSalaryDate = isAfterRaiseDate ? salaryDate : raiseDate;
  return {
    employmentId: row.employmentId,
    version: row.version,
    setSalary: {
      from: format(newSalaryDate, isoFormat),
      components: newSalaryComponents ?? filterFalsyParameters(
        formatSalaryIncreaseValues(row.salaryIncrease),
      ),
      massUpdateId: bulkUpdateId,
    },
  };
};

export const formatTesTablesShiftSalaryToCommand = (
  row: ShiftSalaryRow,
  filterValues: BulkSalaryIncreaseFilterValues,
  bulkUpdateId: string,
): ApplyTesSalaryTableCellParams => {
  const tesId = (filterValues.tes as NameIdOption).id;
  const from = format(
    new Date(filterValues.salaryIncreaseStartDate as Date),
    isoFormat,
  );
  const selection = row.raiseDateSalary.tesSalaryTableSelection;
  if (!selection) {
    throw new Error("TES salary table selection missing from row");
  }
  if (!filterValues.tesSalaryTableTargetId) {
    throw new Error("TES salary table target id missing from filters");
  }

  const filteredSalaryIncrease = filterFalsySalaryParameters(
    formatSalaryIncreaseValues(row.salaryIncrease),
  );
  const isBaseComponent = (code: string) =>
    code === "BASE" || code.startsWith("BASE_");
  const isTableEnforcedComponent = (code: string) =>
    isBaseComponent(code) || code === "Palkkaryhman_mukainen_palkka_(tiedoksi)";
  const salary = Object.fromEntries(
    Object.entries((filteredSalaryIncrease.salary || {}) as SalaryValues).filter(
      ([code]) => !isTableEnforcedComponent(code),
    ),
  );
  const zonedSalaryRaw = (filteredSalaryIncrease.zonedSalary || {}) as Record<
    string,
    SalaryValues
  >;
  const zonedSalary = Object.fromEntries(
    Object.entries(zonedSalaryRaw).map(([zone, components]) => [
      zone,
      Object.fromEntries(
        Object.entries(components).filter(([code]) => !isTableEnforcedComponent(code)),
      ),
    ]),
  );

  return {
    talentId: row.talentId,
    version: row.version,
    workTitleId: row.workTitle.id,
    tesId,
    from,
    tesSalaryTableId: filterValues.tesSalaryTableTargetId,
    rowId: selection.rowId,
    columnId: selection.columnId,
    mode: selection.mode,
    massUpdateId: bulkUpdateId,
    salary,
    ...(Object.keys(zonedSalary).some((zone) => Object.keys(zonedSalary[zone]).length > 0)
      ? { zonedSalary }
      : {}),
  };
};

export const formatTesSalaryTableSelectionSummary = (
  salary: SalaryPerDate,
): string => {
  const selection = salary.tesSalaryTableSelection;
  const mode = selection
    ? (selection.mode || "FULL_SALARY") === "INFO_ONLY"
      ? "INFO"
      : (selection.mode || "FULL_SALARY") === "WITH_COMPS"
        ? "WITH_COMPS"
        : "FULL_SALARY"
    : "";
  const sourceName = salary.tesSalaryTableName || "";
  const sourceDate = salary.tesSalaryTableEffectiveDate
    ? ` ${salary.tesSalaryTableEffectiveDate}`
    : "";
  const selectionLabel =
    salary.tesSalaryTableSelectionLabel ||
    (selection ? `${selection.rowId} / ${selection.columnId}` : "");

  if (salary.targetTesSalaryTableSelectionLabel || salary.targetTesSalaryTableName) {
    const targetName = salary.targetTesSalaryTableName || "";
    const targetDate = salary.targetTesSalaryTableEffectiveDate
      ? ` ${salary.targetTesSalaryTableEffectiveDate}`
      : "";
    const targetSelection = salary.targetTesSalaryTableSelectionLabel || selectionLabel;
    return `${targetName}${targetDate}: ${targetSelection}${mode ? ` (${mode})` : ""}`;
  }

  if (!selectionLabel && !sourceName) return "";
  return `${sourceName}${sourceDate}${sourceName && selectionLabel ? ": " : ""}${selectionLabel}${mode ? ` (${mode})` : ""}`;
};

// import isEqual from "lodash/isEqual"; // or implement your own deepEqual
//
// export const formatKtaToCommand = (
//     row: KtaRow,
//     filterValues: BulkSalaryIncreaseFilterValues,
//     bulkUpdateId: string,
// ): AddAccumDelta | null => {
//   const formattedValues = formatSalaryIncreaseValues(row.salaryIncrease);
//   const originalValue = row.raisedKeskiansio;
//   const newValue = formattedValues["kta"];
//
//   if (isEqual(originalValue, newValue)) {
//     return null; // or undefined, or skip sending
//   }
//
//   // ...rest of your logic
// };

// TODO make somehow use of bulkUpdateId
export const formatKtaToCommand = (
    row: KtaRow,
    filterValues: BulkSalaryIncreaseFilterValues,
    bulkUpdateId: string,
): AddAccumDelta | null => {
  // Salary date can be earliest the salary date to not make
  // too early salary rows for long employments
  const raiseDate = format(filterValues.salaryIncreaseStartDate as Date, isoFormat);
  const formattedValues = formatSalaryIncreaseValues(row.salaryIncrease)
  const raisedKeskiansio = formattedValues["kta"];
  if (Number(row.currentKeskiansio) === Number(raisedKeskiansio)) {
    console.log("KTA keskiansio not changed", row.currentKeskiansio, raisedKeskiansio);
    return null;
  }
  ;
  const accumDataKeskiansio = {
    isInternal: false,
    date: raiseDate,
    contEmpId: row.contEmpId,
    employmentId: row.employmentId,
    bulkUpdateId,
    values: {
      // TODO: change to take value from fields
      keskiansio: raisedKeskiansio || row.raisedKeskiansio,
    },
  };
  return accumDataKeskiansio;
};

export const ktaRowChangedValue = (row: KtaRow): boolean => {
  const formattedValues = formatSalaryIncreaseValues(row.salaryIncrease)
  const raisedKeskiansio = formattedValues["kta"];
  if (Number(row.currentKeskiansio) !== Number(raisedKeskiansio)) {
    return true;
  }
  return ktaOngoingRowChangedValue(row) !== null;
}

export const ktaOngoingRowChangedValue = (row: KtaRow): KtaAvg | null => {
  const formattedValues = formatSalaryIncreaseValues(row.salaryIncrease);
  let hasDiff = false;

  const newKta = Object.fromEntries(Object.keys(row.raisedKta).map((k: string) => {
    let currentKta = row.currentKta[k];
    let formKta = formattedValues[k];
    if (currentKta !== formKta) {
      hasDiff = true;
      let kta = row.currentOngoingKta[k];
      // formatted values are salary/worktime.. now we go from that to salary
      kta.salary = (
          Number(formattedValues[k]) * Number(kta.workTime)
      ).toFixed(8);
      return [k, kta];
    }
    return [k, row.raisedKta[k]];
  }));
  if (hasDiff) {
    return newKta;
  }

  return null;
}

export const formatKtaLaskentaToCommand = (
    row: KtaRow,
    bulkUpdateId: string,
): AddAccumDelta | null => {
  const ktaOngoing: KtaAvg | null = ktaOngoingRowChangedValue(row);
  if (!ktaOngoing) {
    console.log("No changes found");
    return null;
  }
  const ongoingAverages = {
    ongoingAverages: ktaOngoing
  };
  const values = {
    keskiansiolaskenta: JSON.stringify(ongoingAverages),
  };
  const accumData = {
    isInternal: true,
    date: row.raisedKtaDate,
    contEmpId: row.contEmpId,
    employmentId: row.employmentId,
    bulkUpdateId,
    values
  };
  return accumData;
};

export const resolveKtaQueryEndDate = (kta: Kta): string => {
  // TODO if no end date, use 18 months from start date .. but what should we use?
  // Also do we have still accumulations after it? I think we should not.
  return kta.endDate ?? format(addMonths(kta.startDate, 18), isoFormat)
};

export const renderRowStatus = (status: SalaryIncreaseStatus) => {
  const statuses = {
    [SalaryIncreaseStatus.SUCCESS]: "✅",
    [SalaryIncreaseStatus.FAILED]: "❌",
    [SalaryIncreaseStatus.IDLE]: "-",
    [SalaryIncreaseStatus.DONE]: "🤷‍♂️",
  };

  return statuses[status];
};

export function objectToString<
  T extends Record<string, any>,
  K extends keyof T,
>(obj: T, excludeKeys: K[]): string {
  return [...Object.entries(obj)]
    .filter(([key]) => !excludeKeys.includes(key as K)) // Exclude specified keys
    .sort(([key1], [key2]) => key1.localeCompare(key2)) // Use .sort() instead
    .map(([key, value]) => (!!value ? `${key}=${value}` : undefined))
    .filter((item) => !!item) // Filter out undefined values
    .join(";");
}

export function objectToStringIncluded<
  T extends Record<string, any>,
  K extends keyof T,
>(obj: T, includedKeys: K[]): string {
  return [...Object.entries(obj)]
    .filter(([key]) => includedKeys.includes(key as K)) // Exclude specified keys
    .sort(([key1], [key2]) => key1.localeCompare(key2)) // Use .sort() instead
    .map(([key, value]) => (!!value ? `${key}=${value}` : undefined))
    .filter((item) => !!item) // Filter out undefined values
    .join(";");
}

export function objectToValueStringIncluded<
  T extends Record<string, any>,
  K extends keyof T,
>(obj: T, includedKeys: K[]): string {
  return [...Object.entries(obj)]
    .filter(([key]) => includedKeys.includes(key as K)) // Exclude specified keys
    .sort(([key1], [key2]) => key1.localeCompare(key2)) // Use .sort() instead
    .map(([key, value]) => (!!value ? `${value}` : undefined))
    .filter((item) => !!item) // Filter out undefined values
    .join(";");
}

export const rowToId = (row: ShiftSalary): string => {
  return row.talentId + "|" + row.workTitleId
}

export const uiRowToId = (row: ShiftSalaryRow): string => {
  return row.talentId + "|" + row.workTitle.id
}

export const rowEmpToId = (row: EmploymentSalary): string => {
  return row.employmentId;
}

export const uiRowEmpToId = (row: EmploymentSalaryRow): string => {
  return row.employmentId;
}

export const uiRowKtaToId = (row: KtaRow): string => {
  return row.employmentId;
}

export const resolveMassUpdateRowId = (rowId: string, row: SalaryTableRow): MassUpdateStatusRowId => {
  if (isGroupedEmploymentSalaryRow(row)) {
    return uiRowEmpToId(row);
  } else if (isEmploymentSalaryTableRow(row)) {
    //console.log("employment row unclear", row, clarity);
    return uiRowEmpToId(row);
  } else if (isShiftSalaryTableRow(row)) {
    return uiRowToId(row);
  } else if (isKtaRow(row)) {
    return uiRowKtaToId(row);
  } else {
    console.error("Unknown row type for clarity", row);
  }
  return rowId;
}

export const queryToId = (
  query: BulkSalaryIncreaseQuery
): string => {
  // FIXME: is salary keys needed?
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const salaryKeys = Object.keys(query).filter((key) =>
    key.startsWith("salaryFrom_") || key.startsWith("salaryTo_"))
  .map((key) => key as keyof BulkSalaryIncreaseQuery);
  // const ret = objectToString(query, ["isUnclear", "hasContEmp", "baseEqualsGroup", "isIncreaseApplied", "cursor", "pageSize", "mock", ...salaryKeys]);
  const ret = objectToStringIncluded(query, [
    "raiseDate",
    "salaryType",
    "tesId",
    "target",
    "tesSalaryTableSourceId",
    "tesSalaryTableTargetId",
  ])
  // raiseDate=2025-09-01;salaryType=HOURLY;target=EMPLOYMENT_SHIFTS;tesId=tes_id_rakennusalan_toimihenkilot
  console.log("queryToId ret", ret);
  return ret;
};

export const queryToShortId = (
  query: BulkSalaryIncreaseQuery
): string => {
  const ret = objectToValueStringIncluded(query, [
    "raiseDate",
    "salaryType",
    "tesId",
    "target",
    // "tesSalaryTableSourceId",
    // "tesSalaryTableTargetId",
  ])
  console.log("queryToShortId ret", ret);
  return ret;
};



export function chunkArray<T>(arr: T[], chunkSize: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    result.push(arr.slice(i, i + chunkSize));
  }
  return result;
}

export function applyPercentageIncrease(value: number, percent: number): number {
  if (percent === 0) {
    return value;
  }
  const base = Math.round(value * 100); // value in cents
  const increase = Math.round(value * percent); // apply percent directly to cents
  return (base + increase) / 100;
}


export function applyKtaPercentageIncrease(value: number, percent: number): number {
  if (percent === 0) {
    return value;
  }
  const increase = value * percent / 100;
  return value + increase;
}

export function flatMapAsSet<T, U>(
  arr: T[],
  fn: (item: T) => Iterable<U>
): Set<U> {
  const result = new Set<U>();
  for (const item of arr) {
    // console.log("proc", item)
    for (const v of fn(item)) {
      result.add(v);
    }
  }
  return result;
}
